"use client";

import { useState, useEffect } from "react";

interface ShareBarProps {
  /** Override; ellers leses window.location.href */
  url?: string;
  /** Override; ellers leses document.title */
  title?: string;
  /** Override; ellers leses <meta property="og:image"> */
  image?: string;
  /** F.eks. "Del denne oppskriften" / "Del denne artikkelen" */
  label?: string;
}

// Selvkonfigurerende delefelt. Leser URL, tittel og og:bilde fra siden i
// nettleseren, så det kan slippes inn (uten props) på enhver artikkel-,
// verktøy- eller oppskriftsside.
export function ShareBar({ url, title, image, label = "Del dette" }: ShareBarProps) {
  const [u, setU] = useState(url || "");
  const [t, setT] = useState(title || "");
  const [img, setImg] = useState(image || "");
  const [canNative, setCanNative] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setU(url || window.location.href);
    setT(title || document.title);
    if (!image) {
      const og = document.querySelector('meta[property="og:image"]');
      setImg(og?.getAttribute("content") || "");
    }
    setCanNative(typeof navigator !== "undefined" && typeof navigator.share === "function");
  }, [url, title, image]);

  const e = encodeURIComponent;
  const targets = [
    { key: "pinterest", label: "Pinterest", color: "#e60023", href: `https://pinterest.com/pin/create/button/?url=${e(u)}&media=${e(img)}&description=${e(t)}` },
    { key: "facebook", label: "Facebook", color: "#1877f2", href: `https://www.facebook.com/sharer/sharer.php?u=${e(u)}` },
    { key: "whatsapp", label: "WhatsApp", color: "#25d366", href: `https://wa.me/?text=${e(`${t} ${u}`)}` },
    { key: "x", label: "X", color: "#000000", href: `https://twitter.com/intent/tweet?text=${e(t)}&url=${e(u)}` },
  ];

  async function nativeShare() {
    try { await navigator.share({ title: t, url: u }); } catch { /* avbrutt */ }
  }
  async function copy() {
    try {
      await navigator.clipboard.writeText(u);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard utilgjengelig */ }
  }

  const btn = "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium text-white transition-opacity hover:opacity-90 whitespace-nowrap";

  return (
    <div className="border-t border-stone-200 pt-6 mt-10">
      <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-3">{label}</p>
      <div className="flex flex-wrap gap-2">
        {canNative && (
          <button onClick={nativeShare} className={btn} style={{ background: "#1c1917" }}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/></svg>
            Del
          </button>
        )}
        {targets.map((s) => (
          <a key={s.key} href={s.href} target="_blank" rel="noopener noreferrer" className={btn} style={{ background: s.color }}>
            {s.label}
          </a>
        ))}
        <button onClick={copy} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors whitespace-nowrap">
          {copied ? "✓ Kopiert" : "Kopier lenke"}
        </button>
      </div>
      {canNative && (
        <p className="text-xs text-stone-400 mt-2">«Del» åpner delemenyen på mobil — der finner du Instagram, Snapchat og TikTok hvis du har appene.</p>
      )}
    </div>
  );
}
